import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SessionLimitService } from '../services/session-limit.service';
import { AutorizacionesService, Autorizacion } from '../../services/autorizaciones.service';
import { TraspasosService, Traspaso } from '../../services/traspasos.service';
import { BitacoraService, EventoBitacora } from '../../services/bitacora.service';
import { ComunicacionService } from '../../services/comunicacion.service';
import { AlertasService } from '../../services/alertas.service';
import { ConfiguracionService } from '../../services/configuracion.service';
import { LogoComponent } from '../../shared/ui/logo.component';
import { ThemeService } from '../../shared/ui/theme.service';
import { navIcon } from '../../shared/ui/icons';
import { Global } from '../../services/Global';

interface MenuItem {
  label: string;
  route: string;
  icon: string;
  grupos?: string[];
  modulo?: string;
  /**
   * Módulo de escritorio: captura densa, tablas anchas o cierres contables que en
   * un celular se vuelven propensos a error. No se ocultan (el dueño a veces
   * necesita consultarlos desde el teléfono), pero en móvil quedan replegados
   * bajo "Administración" para que el menú de campo no compita con ellos.
   */
  escritorio?: boolean;
}

const MENU_ITEMS: MenuItem[] = [
  // Sin `grupos` un ítem lo ve cualquier rol. Dashboard y Reportes están llenos de
  // importes, así que se listan explícitamente para dejar fuera a MECANICO.
  { label: 'Dashboard', route: '/dashboard', icon: 'ri-dashboard-line', grupos: ['ADMIN', 'ASESOR', 'CAJERO'] },
  { label: 'Talleres', route: '/taller', icon: 'ri-store-2-line', grupos: ['SUPER_ADMIN'], escritorio: true },
  { label: 'Clientes Facturas', route: '/clientes-facturas', icon: 'ri-file-paper-2-line', grupos: ['SUPER_ADMIN'], escritorio: true },
  // Para los talleres, Novedades y Buzón viven en la campana y al pie del menú.
  { label: 'Publicar Novedades', route: '/novedades', icon: 'ri-megaphone-line', grupos: ['SUPER_ADMIN'] },
  { label: 'Buzón de Talleres', route: '/buzon', icon: 'ri-inbox-line', grupos: ['SUPER_ADMIN'] },
  { label: 'Clientes', route: '/clientes', icon: 'ri-user-3-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Vehículos', route: '/vehiculos', icon: 'ri-car-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Flotillas', route: '/flotillas', icon: 'ri-briefcase-4-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Inventario', route: '/inventario', icon: 'ri-archive-2-line', grupos: ['ADMIN', 'ASESOR'], escritorio: true },
  { label: 'Órdenes de Servicio', route: '/ordenes-servicio', icon: 'ri-tools-line', grupos: ['ADMIN', 'ASESOR', 'MECANICO'] },
  { label: 'Cotizaciones', route: '/cotizaciones', icon: 'ri-file-list-3-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Punto de Venta', route: '/punto-venta', icon: 'ri-cash-line', grupos: ['ADMIN', 'ASESOR', 'CAJERO'] },
  { label: 'Cortes de Caja', route: '/cortes-caja', icon: 'ri-safe-2-line', grupos: ['ADMIN', 'CAJERO'] },
  { label: 'Citas', route: '/citas', icon: 'ri-calendar-schedule-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Recordatorios', route: '/recordatorios', icon: 'ri-alarm-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Contabilidad', route: '/contabilidad', icon: 'ri-calculator-line', grupos: ['ADMIN', 'ASESOR'], escritorio: true },
  { label: 'Proveedores', route: '/proveedores', icon: 'ri-truck-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Técnicos', route: '/tecnicos', icon: 'ri-team-line', grupos: ['ADMIN', 'ASESOR'] },
  { label: 'Sucursales', route: '/sucursales', icon: 'ri-map-pin-2-line', grupos: ['ADMIN'], escritorio: true },
  { label: 'Reportes', route: '/reportes', icon: 'ri-bar-chart-2-line', grupos: ['ADMIN', 'ASESOR', 'CAJERO'], escritorio: true },
  { label: 'KPIs', route: '/kpis', icon: 'ri-line-chart-line', grupos: ['ADMIN'], modulo: 'kpis', escritorio: true },
  { label: 'Usuarios', route: '/usuarios', icon: 'ri-group-line', grupos: ['ADMIN'], escritorio: true },
  { label: 'Facturación', route: '/facturacion', icon: 'ri-bill-line', grupos: ['ADMIN'], modulo: 'facturacion', escritorio: true },
  { label: 'Configuración', route: '/configuracion', icon: 'ri-settings-4-line', grupos: ['ADMIN'], escritorio: true },
  { label: 'Pagos', route: '/pagos', icon: 'ri-secure-payment-line', grupos: ['ADMIN'], escritorio: true },
];

/** Debajo de este ancho el menú se comporta como cajón (off-canvas). */
const MOBILE_BREAKPOINT = '(max-width: 1023px)';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, LogoComponent],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.css',
  host: { '(document:keydown.escape)': 'cerrarCajonMovil()' },
})
export class AppShellComponent {
  private readonly authService = inject(AuthService);
  private readonly sessionLimit = inject(SessionLimitService);
  private readonly router = inject(Router);
  private readonly autorizacionesService = inject(AutorizacionesService);
  private readonly bitacoraService = inject(BitacoraService);
  readonly comunicacion = inject(ComunicacionService);
  readonly alertas = inject(AlertasService);
  private readonly traspasosService = inject(TraspasosService);
  private readonly configService = inject(ConfiguracionService);
  readonly themeService = inject(ThemeService);
  readonly version = Global.VERSION;

  /** Ícono de un ítem de menú: `-fill` cuando está activo, `-line` si no. */
  iconFor(item: MenuItem): string {
    return navIcon(item.icon, this.isActive(item.route));
  }

  /** true mientras la ventana esté por debajo de `lg`: el menú es un cajón. */
  esMovil = signal(false);
  /** En móvil los módulos de escritorio arrancan replegados. */
  mostrarEscritorio = signal(false);

  // En escritorio el menú nace abierto; en celular/tablet nace cerrado para no
  // comerse la pantalla (el contenido es lo que el técnico necesita ver).
  sidebarOpen = signal(true);
  switchingBranch = signal(false);
  targetBranchName = signal('');
  currentUser = this.authService.currentUser;

  // Notificaciones
  autorizacionesPendientes = signal<Autorizacion[]>([]);
  traspasosPendientes = signal<Traspaso[]>([]);
  /** Mano de obra cobrada en caja a un precio distinto al de catálogo/OS, sin revisar. */
  preciosManoObra = signal<EventoBitacora[]>([]);
  /**
   * Pendientes del taller (cobranza, piezas por costear, órdenes por cerrar y
   * clientes por contactar). Cada tema cuenta 1 en la campana, no cada documento:
   * un taller con 80 recordatorios no debe ver "80" en rojo para siempre. La
   * cobranza sólo cuenta cuando ya hay créditos vencidos.
   */
  alertasPendientes = computed(() => {
    const a = this.alertas.resumen();
    if (!a) return 0;
    return [
      a.cobranza.vencido.saldo > 0,
      a.costos_pendientes.lineas > 0,
      a.ordenes_por_cerrar.total > 0,
      this.veRecordatorios() && a.recordatorios.total > 0,
    ].filter(Boolean).length;
  });
  /** Recordatorios de servicio: los atiende quien trata con el cliente. */
  veRecordatorios = computed(() => ['ADMIN', 'ASESOR'].includes(this.currentUser()?.user?.grupo || ''));
  totalNotificaciones = computed(() =>
    this.autorizacionesPendientes().length + this.traspasosPendientes().length + this.preciosManoObra().length
    + this.comunicacion.novedadesSinLeer().length + this.comunicacion.respuestasSinLeer().length
    + this.comunicacion.buzonNuevos() + this.alertasPendientes());

  filteredMenuItems = computed(() => {
    const user = this.currentUser();
    const userGroup = user?.user?.grupo || '';
    const userModulos = user?.modulos || [];
    const estado = user?.estadoTaller || 'ACTIVO';

    // Si el taller está inactivo y no es Super Admin, bloquear todo excepto Dashboard (y Pagos si es Admin)
    if (estado === 'INACTIVO' && userGroup !== 'SUPER_ADMIN') {
      if (userGroup === 'ADMIN') {
        return MENU_ITEMS.filter(item => item.route === '/dashboard' || item.route === '/pagos');
      }
      return MENU_ITEMS.filter(item => item.route === '/dashboard');
    }

    // Si es Super Admin, ve Dashboard, Talleres y Clientes Facturas
    if (userGroup === 'SUPER_ADMIN') {
      return MENU_ITEMS.filter(item => ['/dashboard', '/taller', '/clientes-facturas', '/novedades', '/buzon'].includes(item.route));
    }

    return MENU_ITEMS.filter(item => {
      // 1. Validar Roles (Dinámicos desde configuración si existen)
      const permisos = this.configService.permisosModulos();
      const allowedGroups = permisos[item.label] || item.grupos;

      if (allowedGroups && !allowedGroups.includes(userGroup)) return false;

      // 2. Validar Módulos
      // Si el ítem requiere un módulo en específico, verificar si el taller cuenta con él.
      // (O si el admin tiene '*', lo cual significa acceso total).
      if (item.modulo) {
        if (!userModulos.includes('*') && !userModulos.includes(item.modulo)) {
          return false;
        }
      }

      return true;
    });
  });

  /** Menú de operación diaria: lo primero que ve el técnico o el asesor en campo. */
  menuPrincipal = computed(() => this.filteredMenuItems().filter(i => !i.escritorio));
  /** Módulos administrativos pesados (contabilidad, catálogos, configuración). */
  menuEscritorio = computed(() => this.filteredMenuItems().filter(i => !!i.escritorio));

  activeRoute = signal('');

  constructor() {
    // Cajón en móvil / barra fija en escritorio. matchMedia evita listeners de
    // resize y se sincroniza con el breakpoint `lg` de Tailwind.
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia(MOBILE_BREAKPOINT);
      this.aplicarModoPantalla(mq.matches);
      mq.addEventListener('change', e => this.aplicarModoPantalla(e.matches));
    }

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.activeRoute.set((e as NavigationEnd).urlAfterRedirects);
    });

    this.cargarNotificaciones();

    // El shell sólo se monta autenticado: aquí empieza el latido que detecta si
    // otro dispositivo tomó nuestro lugar (máx. 2 sesiones por usuario).
    this.sessionLimit.iniciarMonitoreo();
  }

  private lastRefresh = 0;
  refreshNotificaciones(): void {
    const now = Date.now();
    // Solo refrescar si pasaron más de 30 segundos desde el último hover/refresh
    if (now - this.lastRefresh > 30000) {
      this.lastRefresh = now;
      this.cargarNotificaciones();
    }
  }

  cargarNotificaciones(): void {
    const user = this.currentUser()?.user;
    if (!user) return;

    // Novedades y buzón son por usuario, no por sucursal (y SUPER_ADMIN no tiene sucursal).
    if (user.grupo === 'SUPER_ADMIN') {
      this.comunicacion.refrescarBuzon(true);
    } else {
      this.comunicacion.refrescarNovedades();
      this.comunicacion.refrescarBuzon(false);
    }
    if (!user.sucursalActiva?.id) return;

    // Cargar traspasos entrantes (Todos en la sucursal pueden verlo o solo admins/encargados)
    this.traspasosService.getAll(user.sucursalActiva.id, 'entrantes', 'EN_TRANSITO').subscribe({
      next: (res) => this.traspasosPendientes.set(res?.items || []),
      error: () => { }
    });

    // Pendientes del taller: todo lo que mueve dinero queda fuera para el mecánico.
    if (['ADMIN', 'ASESOR', 'CAJERO'].includes(user.grupo)) this.alertas.refrescar();

    // Solo ADMINs ven autorizaciones
    if (user.grupo !== 'ADMIN' && user.grupo !== 'SUPER_ADMIN') return;

    this.autorizacionesService.getAll('PENDIENTE', user.sucursalActiva.id).subscribe({
      next: (res) => this.autorizacionesPendientes.set(res?.items || []),
      error: () => { }
    });

    this.bitacoraService.pendientes(user.sucursalActiva.id).subscribe({
      next: (items) => this.preciosManoObra.set(items),
      error: () => { }
    });
  }

  descartarNovedad(id: string): void {
    this.comunicacion.marcarLeidas([id]).subscribe();
  }

  verRespuestaBuzon(id: string): void {
    this.comunicacion.actualizarBuzon(id, { respuesta_leida: true }).subscribe();
    this.router.navigate(['/buzon']);
  }

  /** Visto bueno del admin: el evento sigue en la bitácora, sólo deja de notificarse. */
  revisarPrecioManoObra(id: string): void {
    this.bitacoraService.revisar(id).subscribe({
      next: () => this.preciosManoObra.update(list => list.filter(e => e.id !== id)),
      error: (err) => {
        // 409 = otro admin ya lo revisó: igual se quita de la lista.
        if (err?.status === 409) this.preciosManoObra.update(list => list.filter(e => e.id !== id));
      }
    });
  }

  responderAutorizacion(id: string, estado: 'APROBADA' | 'RECHAZADA'): void {
    this.autorizacionesService.update(id, estado).subscribe({
      next: () => {
        this.autorizacionesPendientes.update(auths => auths.filter(a => a.id !== id));
      }
    });
  }

  confirmarTraspaso(t: Traspaso): void {
    const items_recibidos = t.items.map(i => ({ item_id: i.item_id, cantidad_recibida: i.cantidad, merma: 0 }));
    this.traspasosService.receive(t.id!, { estado: 'COMPLETADO', items_recibidos }).subscribe({
      next: () => {
        this.traspasosPendientes.update(list => list.filter(tr => tr.id !== t.id));
      }
    });
  }

  private aplicarModoPantalla(esMovil: boolean): void {
    this.esMovil.set(esMovil);
    // Al pasar a móvil el cajón se cierra; al volver a escritorio la barra se
    // despliega otra vez (es su estado natural con espacio de sobra).
    this.sidebarOpen.set(!esMovil);
    if (!esMovil) this.mostrarEscritorio.set(false);
  }

  toggleSidebar(): void {
    this.sidebarOpen.update(v => !v);
  }

  /** Escape cierra el cajón en móvil (en escritorio la barra es fija y no se toca). */
  cerrarCajonMovil(): void {
    if (this.esMovil() && this.sidebarOpen()) this.sidebarOpen.set(false);
  }

  /** En móvil, navegar cierra el cajón: si no, tapa la pantalla que se acaba de abrir. */
  onNavClick(): void {
    this.refreshNotificaciones();
    if (this.esMovil()) this.sidebarOpen.set(false);
  }

  isActive(route: string): boolean {
    return this.activeRoute().startsWith(route);
  }

  async logout(): Promise<void> {
    this.sessionLimit.detenerMonitoreo();
    await this.authService.logout();
  }

  async setSucursal(sucursal: any): Promise<void> {
    if (this.currentUser()?.user?.sucursalActiva?.id === sucursal.id) return;

    // Cerrar el dropdown automáticamente
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    this.targetBranchName.set(sucursal.nombre);
    this.switchingBranch.set(true);

    // Simular un tiempo de transición para que el usuario perciba el cambio
    await new Promise(resolve => setTimeout(resolve, 1500));

    this.authService.setSucursalActiva(sucursal);
    this.switchingBranch.set(false);

    // Opcionalmente recargar la ruta actual para refrescar datos
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  get userIcon(): string {
    const grupo = this.currentUser()?.user?.grupo || 'ASESOR';
    const mapping: Record<string, string> = {
      'SUPER_ADMIN': 'ri-shield-user-line',
      'ADMIN': 'ri-user-settings-line',
      'ASESOR': 'ri-customer-service-2-line',
      'MECANICO': 'ri-tools-line',
      'CAJERO': 'ri-money-dollar-box-line'
    };
    return mapping[grupo] ?? 'ri-user-3-line';
  }

  missingSucursal = computed(() => {
    const user = this.currentUser();
    if (!user || user.user.grupo === 'SUPER_ADMIN') return false;
    return !user.user.sucursalActiva;
  });

  async refreshProfile(): Promise<void> {
    const success = await this.authService.checkSession();
    if (success) {
      this.cargarNotificaciones();
    }
  }
}
